import { createAsyncThunk } from '@reduxjs/toolkit';
import { matchPath } from 'react-router-dom';

import { ensureFetchDelay } from '../../helpers/fetch';
import svpApiClient from '../../services/svpApiClient';
import { parseParamsFromUrl } from '../../services/svp/apiClient';
import { config } from '../../config';
import { SvpFilters } from '../../services/svp/filters';
import { getUserId } from '../../services/schibstedAccount';
import { isMobile } from '../../utils/device';

import type { SvpFilter } from '../../services/svp/filters';
import type {
  GetAssetsArgs,
  GetMostSeenAssetsArgs,
  GetSeasonAssetsArgs,
  SearchAssetsArgs,
} from '../../services/svp/apiClient';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { SvpCategory } from '@vgtv/api-client/lib/svp_category';
import type {
  SvpAsset,
  SvpAssetAdditional,
} from '@vgtv/api-client/lib/svp_asset';
import type { Vendor } from '../../types';

const extractParamsFromDataUrl = <T extends { filters?: SvpFilters }>({
  dataUrl,
  ...params
}: T & { dataUrl?: string }) => {
  if (!dataUrl || dataUrl.indexOf('?') < 0) {
    return params;
  }

  const parsed = parseParamsFromUrl<T & { limit?: number }>(dataUrl);
  return {
    ...parsed,
    ...params,
    ...(parsed.limit && { limit: parsed.limit }),
    filters: SvpFilters.from(params.filters).concat(parsed.filters),
  };
};

const getBlendUrl = async (dataUrl: string) => {
  const userId = await getUserId();
  const url = new URL(dataUrl);
  url.searchParams.append('origin', isMobile ? 'mobile' : 'desktop');

  if (userId) {
    url.searchParams.append('userId', userId);
  }
  return url;
};

const fetchAssetsByIds = async (
  assetIds: string[],
  vendor: Vendor = 'vgtv'
) => {
  const filters = SvpFilters.from([
    {
      property: 'assetId',
      operator: '::',
      value: assetIds.join(','),
    } as SvpFilter,
  ]);
  return svpApiClient.getAssets({
    filters,
    vendor,
  });
};

const fetchAssetsFromBlend = async (params: { dataUrl: string }) => {
  const url = await getBlendUrl(params.dataUrl);

  const res = await fetch(url);
  if (res.ok) {
    const assetIds = ((await res.json()) as { items: [{ id: string }] }).items
      .slice(0, 10)
      .map(({ id }) => id) as string[];
    return fetchAssetsByIds(assetIds, 'vgtv');
  }

  throw new Error(`Failed to fetch assets from Blend: ${res.statusText}`);
};

const fetchSvpAssets = createAsyncThunk(
  'assets/fetchSvpAssets',
  ({
    filter,
    ...params
  }: Omit<
    | SearchAssetsArgs
    | GetMostSeenAssetsArgs
    | GetAssetsArgs
    | GetSeasonAssetsArgs,
    'filters'
  > & { dataUrl: string; filter?: string; sort?: string; query?: string }) =>
    ensureFetchDelay(() => {
      if (params.dataUrl.indexOf('/curate/blender') >= 0) {
        return fetchAssetsFromBlend(params);
      }
      const input = {
        ...params,
        filters: SvpFilters.from(filter),
      };

      if (params.dataUrl.indexOf('/search') >= 0) {
        const args = extractParamsFromDataUrl<SearchAssetsArgs>(input);
        return svpApiClient.searchAssets(args);
      }

      if (params.dataUrl.indexOf('/assets/most-seen') >= 0) {
        const args = extractParamsFromDataUrl<GetMostSeenAssetsArgs>(input);
        return svpApiClient.getMostSeenAssets(args);
      }

      const match = matchPath<{ categoryId: string; seasonNumber: string }>(
        params.dataUrl.split('?')[0],
        `${config.svpApiHostname}${params.vendor}/categories/:categoryId/seasons/:seasonNumber/assets`
      );

      if (match) {
        const args = extractParamsFromDataUrl<
          Omit<
            GetSeasonAssetsArgs & { filters?: SvpFilters },
            'categoryId' | 'seasonNumber'
          >
        >(
          input as Omit<GetSeasonAssetsArgs, 'categoryId' | 'seasonNumber'> & {
            dataUrl: string;
            filters: SvpFilters;
          }
        );

        return svpApiClient.getSeasonAssets({
          ...args,
          categoryId: parseInt(
            match.params.categoryId,
            10
          ) as SvpCategory['id'],
          seasonNumber: parseInt(match.params.seasonNumber, 10),
        });
      }

      const args = extractParamsFromDataUrl<GetAssetsArgs>(input);
      return svpApiClient.getAssets(args);
    })
);

const fetchSvpAssetById = createAsyncThunk(
  'assets/fetchSvpAssetById',
  ({
    assetId,
    vendor,
  }: {
    assetId: SvpAsset['id'];
    vendor: Vendor;
  }): Promise<SvpAssetAdditional> => {
    return svpApiClient.getAsset({ assetId, vendor });
  }
);

const fetchSvpAssetsByIds = createAsyncThunk(
  'assets/fetchSvpAssetsById',
  ({
    assetsIds,
    vendor,
    limit = 10,
  }: {
    assetsIds: SvpAsset['id'][];
    vendor: Vendor;
    limit?: number;
  }): Promise<{ _embedded: { assets: SvpAssetAdditional[] } }> => {
    return ensureFetchDelay(
      svpApiClient.getAssets({
        filters: SvpFilters.from(`assetId::${assetsIds.join(',')}`),
        vendor,
        limit,
      })
    );
  }
);

const fetchSvpAssetCategoryMatch = createAsyncThunk(
  'assets/fetchSvpAssetCategoryMatch',
  async ({
    asset,
    categoryId,
  }: {
    asset: SvpAsset;
    categoryId: SvpCategory['id'];
  }) => {
    if (
      asset.category.id === categoryId ||
      asset.category.parentId === categoryId
    ) {
      return { result: true };
    }

    if (asset.category.parentId === 0) {
      return { result: false };
    }

    const isCategoryMatched = await svpApiClient.checkCategoryMatch({
      assetId: asset.id,
      vendor: asset.provider,
      categoryId,
    });

    return { result: isCategoryMatched };
  }
);

const fetchLiveSvpAssets = createAsyncThunk(
  'assets/fetchLiveSvpAssets',
  async (
    {
      limit,
      vendor,
      categoryId,
      tagId,
      startDate,
      endDate,
    }: {
      limit: number;
      vendor: Vendor;
      categoryId?: SvpCategory['id'];
      tagId?: CnpTag['id'];
      startDate?: number;
      endDate?: number;
    },
    thunkAPI
  ) => {
    const dispatch = thunkAPI.dispatch;

    const searchParameters = {
      dataUrl: '/search',
      limit,
      vendor,
      filter: [
        `streamType::live,wasLive`,
        categoryId && `categoryId::${categoryId}`,
        tagId && `tagsid::${tagId}`,
        startDate && `flightTimes.start>=${startDate}`,
        endDate && `flightTimes.start<=${endDate}`,
      ]
        .filter((f): f is string => Boolean(f))
        .join('|'),
    };

    let {
      total_items: count,
      _embedded: { assets = [] },
      _links: { next: { href } = {} },
    } = await dispatch(fetchSvpAssets(searchParameters)).unwrap();

    // Load more pages
    while (href !== undefined && count === limit) {
      const response = await dispatch(
        fetchSvpAssets({ dataUrl: href!, vendor })
      ).unwrap();
      assets = [...assets, ...response._embedded.assets];
      href = response._links.next?.href;
      count = response.total_items;
    }

    return assets as SvpAssetAdditional[];
  }
);

export {
  fetchSvpAssetById,
  fetchSvpAssetsByIds,
  fetchSvpAssets,
  fetchSvpAssetCategoryMatch,
  fetchLiveSvpAssets,
};
